import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Heading1, Heading2, Code } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PostModal = ({ isOpen, onClose, post, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'blog',
    excerpt: '',
    cover_image: '',
    podcast_url: '',
    active: true
  });
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title || '',
        content: post.content || '',
        type: post.type || 'blog',
        excerpt: post.excerpt || '',
        cover_image: post.cover_image || '',
        podcast_url: post.podcast_url || '',
        active: post.active !== undefined ? post.active : true
      });
    } else {
      setFormData({
        title: '',
        content: '',
        type: 'blog',
        excerpt: '',
        cover_image: '',
        podcast_url: '',
        active: true
      });
    }
  }, [post, isOpen]);

  // Formázó függvények
  const insertFormatting = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    const beforeText = formData.content.substring(0, start);
    const afterText = formData.content.substring(end);

    const newContent = beforeText + before + selectedText + after + afterText;
    setFormData({ ...formData, content: newContent });

    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + before.length + selectedText.length + after.length;
    }, 0);
  };

  const formatBold = () => insertFormatting('<strong>', '</strong>');
  const formatItalic = () => insertFormatting('<em>', '</em>');
  const formatUnderline = () => insertFormatting('<u>', '</u>');
  const formatH1 = () => insertFormatting('<h1>', '</h1>');
  const formatH2 = () => insertFormatting('<h2>', '</h2>');
  const formatParagraph = () => insertFormatting('<p>', '</p>');
  const formatList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    // Ha van kijelölt szöveg, minden sort lista elemmé alakít
    if (selectedText) {
      const lines = selectedText.split('\n').filter(line => line.trim());
      const listItems = lines.map(line => `  <li>${line.trim()}</li>`).join('\n');
      const beforeText = formData.content.substring(0, start);
      const afterText = formData.content.substring(end);
      
      const newContent = beforeText + '<ul>\n' + listItems + '\n</ul>' + afterText;
      setFormData({ ...formData, content: newContent });
    } else {
      insertFormatting('<ul>\n  <li>', '</li>\n</ul>');
    }
  };
  const formatOrderedList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    // Ha van kijelölt szöveg, minden sort lista elemmé alakít
    if (selectedText) {
      const lines = selectedText.split('\n').filter(line => line.trim());
      const listItems = lines.map(line => `  <li>${line.trim()}</li>`).join('\n');
      const beforeText = formData.content.substring(0, start);
      const afterText = formData.content.substring(end);
      
      const newContent = beforeText + '<ol>\n' + listItems + '\n</ol>' + afterText;
      setFormData({ ...formData, content: newContent });
    } else {
      insertFormatting('<ol>\n  <li>', '</li>\n</ol>');
    }
  };
  const formatCode = () => insertFormatting('<code>', '</code>');
  const formatLink = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    // Ha nincs kijelölt szöveg, kérjük be
    let linkText = selectedText;
    if (!linkText.trim()) {
      linkText = prompt('Add meg a link szövegét (pl. "Kattints ide"):');
      if (!linkText) return;
    }
    
    const url = prompt('Add meg a link URL-t (pl. https://example.com):');
    if (!url) return;
    
    const beforeText = formData.content.substring(0, start);
    const afterText = formData.content.substring(end);
    const newContent = beforeText + `<a href="${url}" target="_blank">${linkText}</a>` + afterText;
    
    setFormData({ ...formData, content: newContent });
    
    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      const newPos = start + `<a href="${url}" target="_blank">${linkText}</a>`.length;
      textarea.selectionStart = textarea.selectionEnd = newPos;
    }, 0);
  };
  const formatImage = () => {
    const url = prompt('Add meg a kép URL-t (pl. https://example.com/kep.jpg):');
    if (!url) return;
    
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const beforeText = formData.content.substring(0, start);
    const afterText = formData.content.substring(start);
    
    const imgTag = `<img src="${url}" alt="kép" style="max-width: 100%; border-radius: 8px;" />\n`;
    const newContent = beforeText + imgTag + afterText;
    
    setFormData({ ...formData, content: newContent });
    
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + imgTag.length;
    }, 0);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/posts/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        cover_image: response.data.url
      }));
    } catch (error) {
      alert('Hiba történt a kép feltöltése során');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 overflow-y-auto">
      <div className="glass rounded-2xl max-w-4xl w-full my-8">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-2xl font-semibold">
            {post ? 'Bejegyzés szerkesztése' : 'Új bejegyzés'}
          </h2>
          <button onClick={onClose} className="text-2xl hover:text-yellow-200">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Cím *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                placeholder="A bejegyzés címe"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Típus *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              >
                <option value="blog">Blog cikk</option>
                <option value="podcast">Podcast</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Rövid leírás</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none"
              placeholder="Rövid összefoglaló a listához..."
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Tartalom *</label>
            
            {/* Formázó toolbar */}
            <div className="flex flex-wrap gap-1 mb-2 p-2 bg-white/5 border border-white/10 rounded-lg">
              <button
                type="button"
                onClick={formatBold}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Kövér (strong)"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={formatItalic}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Dőlt (em)"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={formatUnderline}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Aláhúzott (u)"
              >
                <Underline className="w-4 h-4" />
              </button>
              <div className="w-px bg-white/10 mx-1"></div>
              <button
                type="button"
                onClick={formatH1}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Főcím (h1)"
              >
                <Heading1 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={formatH2}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Alcím (h2)"
              >
                <Heading2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={formatParagraph}
                className="p-2 hover:bg-white/10 rounded transition text-xs font-bold"
                title="Bekezdés (p)"
              >
                P
              </button>
              <div className="w-px bg-white/10 mx-1"></div>
              <button
                type="button"
                onClick={formatList}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Felsorolás (ul)"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={formatOrderedList}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Számozott lista (ol)"
              >
                <ListOrdered className="w-4 h-4" />
              </button>
              <div className="w-px bg-white/10 mx-1"></div>
              <button
                type="button"
                onClick={formatLink}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Link hozzáadása (a)"
              >
                <LinkIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={formatImage}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Kép beillesztése (img)"
              >
                <ImageIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={formatCode}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Kód (code)"
              >
                <Code className="w-4 h-4" />
              </button>
            </div>

            {/* Content textarea */}
            <textarea
              ref={textareaRef}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              rows={12}
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none font-mono text-sm"
              placeholder="Írd meg a tartalmat HTML formázással..."
            />
            <p className="text-xs text-gray-500 mt-2">
              💡 <strong>Tipp:</strong> 
              <br/>• Jelöld ki a szöveget és kattints a gombokra
              <br/>• <strong>🔗 Link:</strong> Jelöld ki a szöveget (pl. "ide kattints"), majd kattints a 🔗 gombra. Ha nincs kijelölve, megkérdezi a szöveget is.
              <br/>• <strong>🖼️ Kép:</strong> Kattints a 🖼️ gombra és add meg a teljes URL-t
              <br/>• <strong>📋 Lista:</strong> Jelölj ki több sort és kattints a 📋 gombra
              <br/>• <strong>Enter</strong> = új sor
            </p>
          </div>

          {formData.type === 'podcast' && (
            <div>
              <label className="block text-sm text-gray-400 mb-2">Podcast URL</label>
              <input
                type="url"
                value={formData.podcast_url}
                onChange={(e) => setFormData({ ...formData, podcast_url: e.target.value })}
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                placeholder="https://example.com/podcast.mp3"
              />
            </div>
          )}

          {/* Cover Image Upload */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Borítókép</label>
            
            {formData.cover_image && (
              <div className="mb-3 relative group inline-block">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${formData.cover_image}`}
                  alt="Cover"
                  className="w-48 h-32 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, cover_image: '' })}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg cursor-pointer transition">
              <Upload className="w-5 h-5" />
              <span>{uploading ? 'Feltöltés...' : formData.cover_image ? 'Kép cseréje' : 'Kép feltöltése'}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-300">Aktív (látható a felhasználóknak)</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-lg transition"
            >
              Mégse
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-yellow-200 text-black font-semibold rounded-lg hover:bg-yellow-300 transition"
            >
              Mentés
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.08), hsl(0 0% 100% / 0.04));
          border: 1px solid hsl(0 0% 100% / 0.1);
          backdrop-filter: blur(12px);
        }
      `}</style>
    </div>
  );
};

export default PostModal;
